import { Check, Minus, Plus } from 'lucide-react'
import { type ComponentProps, type ReactNode, useState } from 'react'

import { cn } from '@/lib/cn'

import { IconButton } from './Button'

interface QuantityControlProps {
  value: number
  min?: number
  max?: number
  onChange: (value: number) => void
  label?: string
}

export function QuantityControl({
  value,
  min = 1,
  max = 20,
  onChange,
  label = 'Quantity',
}: QuantityControlProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex items-center gap-1 rounded-control border border-line-strong bg-surface p-1"
    >
      <IconButton
        label="Decrease quantity"
        variant="soft"
        className="size-10 rounded-sm"
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        <Minus className="size-4" />
      </IconButton>
      <span
        aria-live="polite"
        className="min-w-8 text-center text-base font-bold text-ink tabular-nums"
      >
        {/* Keyed step: each change ticks in like an odometer digit */}
        <span key={value} className="inline-block animate-tick-up">
          {value}
        </span>
      </span>
      <IconButton
        label="Increase quantity"
        variant="soft"
        className="size-10 rounded-sm"
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        <Plus className="size-4" />
      </IconButton>
    </div>
  )
}

/* Six drawn cells over ONE native input: paste, iOS OTP autofill and screen
   readers keep working (the real input spans the whole control, invisibly),
   while each digit lands in its own box with a live caret. */
export function OtpInput({ className, ...rest }: ComponentProps<'input'>) {
  const [focused, setFocused] = useState(false)
  const value = typeof rest.value === 'string' ? rest.value : ''
  const activeIndex = Math.min(value.length, 5)
  return (
    <div className={cn('relative', className)}>
      <div aria-hidden="true" className="grid grid-cols-6 gap-2">
        {Array.from({ length: 6 }, (_, index) => {
          const digit = value[index]
          const active = focused && index === activeIndex && !rest.disabled
          return (
            <span
              key={index}
              className={cn(
                'grid min-h-14 place-items-center rounded-control border bg-surface text-2xl font-bold text-ink tabular-nums transition duration-200',
                active
                  ? 'border-brand ring-4 ring-brand/12'
                  : digit
                    ? 'border-line-strong'
                    : 'border-line',
              )}
            >
              {digit ? (
                <span key={digit + index} className="animate-pop">
                  {digit}
                </span>
              ) : active ? (
                <span className="h-6 w-0.5 animate-caret-blink rounded-full bg-brand motion-reduce:animate-none" />
              ) : null}
            </span>
          )
        })}
      </div>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        {...rest}
        onFocus={(event) => {
          setFocused(true)
          rest.onFocus?.(event)
        }}
        onBlur={(event) => {
          setFocused(false)
          rest.onBlur?.(event)
        }}
        /* The functional layer: full-size, invisible, still the target of
           taps, paste and platform OTP autofill. Font stays ≥16px so iOS
           never pinch-zooms the focused field. */
        className="absolute inset-0 size-full cursor-text text-base [caret-color:transparent] opacity-0"
      />
    </div>
  )
}

interface StepIndicatorProps {
  steps: string[]
  current: number
}

export function StepIndicator({ steps, current }: StepIndicatorProps) {
  return (
    <ol className="flex items-center gap-1.5" aria-label="Checkout progress">
      {steps.map((step, index) => {
        const state =
          index < current ? 'done' : index === current ? 'current' : 'ahead'
        return (
          <li
            key={step}
            aria-current={state === 'current' ? 'step' : undefined}
            className="flex min-w-0 items-center gap-1.5"
          >
            <span
              className={cn(
                'grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold',
                state === 'done' && 'bg-brand text-on-brand',
                state === 'current' && 'bg-brand text-on-brand',
                state === 'ahead' && 'bg-surface-tint-strong text-ink-soft',
              )}
            >
              {state === 'done' ? <Check className="size-3.5" /> : index + 1}
            </span>
            <span
              className={cn(
                'truncate text-sm',
                state === 'current'
                  ? 'font-bold text-ink'
                  : 'font-semibold text-ink-faint',
              )}
            >
              {step}
            </span>
            {index < steps.length - 1 && (
              <span aria-hidden="true" className="h-px w-4 bg-line-strong" />
            )}
          </li>
        )
      })}
    </ol>
  )
}

interface MeterProps {
  value: number
  max: number
  label: string
  detail?: ReactNode
}

export function Meter({ value, max, label, detail }: MeterProps) {
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0
  return (
    <div className="grid gap-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-semibold text-ink">{label}</span>
        {detail && <span className="text-ink-soft">{detail}</span>}
      </div>
      <div
        role="meter"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
        className="h-2 overflow-hidden rounded-full bg-surface-tint-strong"
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width]',
            ratio <= 0.2 ? 'bg-warning' : 'bg-brand',
          )}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
    </div>
  )
}

/* Fixed-feeling CTA that stays in the scroll flow: sticky above the bottom
   navigation. Content scrolls beneath a soft blur-and-fade edge instead of a
   hard divider, so the bar reads as floating chrome. */
export function StickyCtaBar({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-30 -mx-5 mt-auto [mask-image:linear-gradient(to_top,black_calc(100%-2rem),transparent)] px-5 pt-8 pb-4 backdrop-blur-sm">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-t from-canvas via-canvas/85 to-transparent" />
      {children}
    </div>
  )
}
